import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStripe } from '@stripe/react-stripe-js';
import axios from 'axios';
import { apiBaseUrl } from 'App';
import { getCsrfToken, setCsrfToken } from 'services/authentication/csrfFunctions';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import PrimaryButton from 'components/buttons/PrimaryButton';
import logoGrey from 'assets/crafter-logo-grey.png';
import '../../style/scss/account.scss';

interface OutstandingInvoice {
    company_name: string;
    invoice_number: string;
    amount_due: string;
    currency: string;
}

const PayInvoicePage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const stripe = useStripe();
    const { isLoggedIn } = useAuthContext();

    // Get the environment hash from the route params
    const { environmentHash } = useParams<{ environmentHash: string }>();

    // Set the state of the page
    const [invoice, setInvoice] = useState<OutstandingInvoice | null>(null);
    const [pageState, setPageState] = useState<'loading' | 'open' | 'processing' | 'processing-slow' | 'success' | 'nothing-due'>('loading');
    const [paymentFailed, setPaymentFailed] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Storage key which remembers the started payment intent across reloads
    const intentStorageKey = `outstanding_payment_intent_${environmentHash}`;

    // Format the amount due with the locale separators and currency symbol
    const formatAmount = (amount: string, currency: string) => {
        return new Intl.NumberFormat(i18n.language || 'nl-NL', {
            style: 'currency',
            currency: currency || 'EUR'
        }).format(parseFloat(amount));
    };

    // Fetch the outstanding invoice, returns whether an invoice is still open
    const fetchOutstandingInvoice = async (): Promise<boolean | null> => {
        try {
            await setCsrfToken();
            const response = await axios.get(
                `${apiBaseUrl}/get_outstanding_invoice/${environmentHash}/`,
                { withCredentials: true }
            );
            setInvoice(response.data);
            return true;
        } catch (requestError: any) {
            if (requestError.response?.status === 404) {
                return false;
            }
            setError(t('account.pay_invoice_load_error'));
            return null;
        }
    };

    // Check the payment status of the intent in the backend, which verifies it in stripe
    const checkPaymentStatus = async (intentId: string): Promise<'paid' | 'processing' | 'open' | null> => {
        try {
            const response = await axios.get(
                `${apiBaseUrl}/get_outstanding_invoice_payment_status/${environmentHash}/`,
                { withCredentials: true, params: { payment_intent: intentId } }
            );
            return response.data.status ?? null;
        } catch {
            return null;
        }
    };

    // Show the success state and forget the finished payment intent
    const showSuccess = () => {
        sessionStorageRemoveSafe();
        setPageState('success');
    };

    // Show the open invoice, optionally with a failure note
    const showOpenInvoice = async (failed: boolean) => {
        sessionStorageRemoveSafe();
        setPaymentFailed(failed);
        const invoiceIsOpen = await fetchOutstandingInvoice();
        setPageState(invoiceIsOpen === false ? 'nothing-due' : 'open');
    };

    // Remove the stored intent without breaking on blocked storage
    const sessionStorageRemoveSafe = () => {
        try {
            sessionStorage.removeItem(intentStorageKey);
        } catch { /* Storage can be blocked in private windows */ }
    };

    // Poll the payment status until it is paid or definitively open
    const pollPaymentStatus = (intentId: string, attempt: number) => {
        pollTimerRef.current = setTimeout(async () => {
            const paymentStatus = await checkPaymentStatus(intentId);

            // Show the success state once the payment is registered
            if (paymentStatus === 'paid') {
                showSuccess();
                return;
            }

            // Show the open invoice with a failure note when the payment failed
            if (paymentStatus === 'open') {
                await showOpenInvoice(true);
                return;
            }

            // Keep polling for a minute, then show that it takes longer
            if (attempt < 20) {
                pollPaymentStatus(intentId, attempt + 1);
            } else {
                setPageState('processing-slow');
            }
        }, 3000);
    };

    // Determine the page state on mount, checking a started payment first
    useEffect(() => {
        const initialize = async () => {

            // Get the payment parameters from the url after returning from the bank
            const url = new URL(window.location.href);
            const redirectStatus = url.searchParams.get('redirect_status');
            const paymentIntentParam = url.searchParams.get('payment_intent');

            // Remove the payment parameters from the url so a refresh starts clean
            if (redirectStatus || paymentIntentParam) {
                window.history.replaceState({}, '', url.pathname);
            }

            // Remember the returned intent so a reload can still check its status
            if (paymentIntentParam) {
                try {
                    sessionStorage.setItem(intentStorageKey, paymentIntentParam);
                } catch { /* Storage can be blocked in private windows */ }
            }

            // Get the intent to check from the url or an earlier started payment
            let storedIntentId: string | null = null;
            try {
                storedIntentId = sessionStorage.getItem(intentStorageKey);
            } catch { /* Storage can be blocked in private windows */ }
            const intentId = paymentIntentParam || storedIntentId;

            // Check the payment status in stripe when an intent is known
            if (intentId) {
                const paymentStatus = await checkPaymentStatus(intentId);

                // Show the success state when the payment is registered
                if (paymentStatus === 'paid') {
                    showSuccess();
                    return;
                }

                // Keep showing the processing state while the bank processes the payment
                if (paymentStatus === 'processing') {
                    setPageState('processing');
                    pollPaymentStatus(intentId, 0);
                    return;
                }

                // Otherwise the invoice is still open, note an aborted or failed payment
                await showOpenInvoice(redirectStatus !== null && redirectStatus !== 'succeeded');
                return;
            }

            // Without a known payment load the outstanding invoice details
            const invoiceIsOpen = await fetchOutstandingInvoice();
            setPageState(invoiceIsOpen === false ? 'nothing-due' : 'open');
        };
        initialize();

        // Clear a running poll timer when the page unmounts
        return () => {
            if (pollTimerRef.current) {
                clearTimeout(pollTimerRef.current);
            }
        };
    }, [environmentHash]);

    // Starts the ideal payment for the outstanding invoice
    const handlePayInvoice = async () => {
        if (!stripe) return;
        setLoading(true);
        setError(null);
        setPaymentFailed(false);

        try {
            // Create the payment intent in the backend
            const response = await axios.post(
                `${apiBaseUrl}/post_outstanding_invoice_payment/${environmentHash}/`,
                {},
                {
                    withCredentials: true,
                    headers: { 'X-CSRFToken': getCsrfToken() },
                }
            );

            // Remember the started intent so a reload can check its status
            const clientSecret: string = response.data.client_secret;
            try {
                sessionStorage.setItem(intentStorageKey, clientSecret.split('_secret')[0]);
            } catch { /* Storage can be blocked in private windows */ }

            // Confirm the ideal payment, this redirects to the bank
            await stripe.confirmIdealPayment(clientSecret, {
                payment_method: { ideal: {} },
                return_url: `${window.location.origin}/pay/${environmentHash}`
            });
        } catch (requestError) {
            console.error(requestError);
            setError(t('account.pay_invoice_payment_error'));
        } finally {
            setLoading(false);
        }
    };

    // Render the content of the payment card based on the page state
    const renderCardContent = () => {

        // Show a loader while the invoice details are fetched
        if (pageState === 'loading') {
            return <p>{t('account.pay_invoice_loading')}</p>;
        }

        // Show that the payment is being processed after returning from the bank
        if (pageState === 'processing') {
            return (
                <>
                    <div className='loader' />
                    <h4>{t('account.pay_invoice_processing_title')}</h4>
                    <p>{t('account.pay_invoice_processing_description')}</p>
                </>
            );
        }

        // Show that the processing takes longer with a refresh button
        if (pageState === 'processing-slow') {
            return (
                <>
                    <div className='loader' />
                    <h4>{t('account.pay_invoice_processing_title')}</h4>
                    <p>{t('account.pay_invoice_processing_slow_description')}</p>
                    <PrimaryButton
                        label='account.pay_invoice_refresh_button_label'
                        size='medium'
                        onClick={() => window.location.reload()}
                    />
                </>
            );
        }

        // Show the success state once the payment is registered
        if (pageState === 'success') {
            return (
                <>
                    <div className='pay-invoice-icon'>👍</div>
                    <h4>{t('account.pay_invoice_success_title')}</h4>
                    <p>{t('account.pay_invoice_success_description')}</p>
                    {!isLoggedIn && (
                        <PrimaryButton
                            label='account.login_button_label'
                            size='medium'
                            onClick={() => window.location.assign('/login')}
                        />
                    )}
                </>
            );
        }

        // Show that no invoices are outstanding anymore
        if (pageState === 'nothing-due' || !invoice) {
            return (
                <>
                    <h4>{t('account.pay_invoice_header_label')}</h4>
                    {error ? (
                        <div className='alert form-alert alert-danger' role='alert'>{error}</div>
                    ) : (
                        <>
                            <p>{t('account.pay_invoice_nothing_due')}</p>
                            {!isLoggedIn && (
                                <PrimaryButton
                                    label='account.login_button_label'
                                    size='medium'
                                    onClick={() => window.location.assign('/login')}
                                />
                            )}
                        </>
                    )}
                </>
            );
        }

        // Show the open invoice with the ideal payment button
        return (
            <>
                <h4>{t('account.pay_invoice_header_label')}</h4>
                <p>{t('account.pay_invoice_description', {
                    company_name: invoice.company_name,
                    invoice_number: invoice.invoice_number,
                })}</p>

                <div className='pay-invoice-amount'>
                    <span className='amount-label'>{t('account.pay_invoice_amount_label')}</span>
                    <span className='amount-value'>{formatAmount(invoice.amount_due, invoice.currency)}</span>
                    <span className='invoice-number'>{t('account.pay_invoice_number_label', { invoice_number: invoice.invoice_number })}</span>
                </div>

                {paymentFailed && (
                    <div className='alert form-alert alert-danger' role='alert'>{t('account.pay_invoice_cancelled_message')}</div>
                )}

                {error && (
                    <div className='alert form-alert alert-danger' role='alert'>{error}</div>
                )}

                <PrimaryButton
                    label='account.pay_invoice_button_label'
                    size='large'
                    onClick={handlePayInvoice}
                    loading={loading}
                />

                <p className='pay-invoice-note'>{t('account.pay_invoice_after_payment_note')}</p>
            </>
        );
    };

    return (
        <div className='container-center'>
            <div className='account-container'>
                <img src={logoGrey} alt="Logo" className='account-logo'/>
                <div className='account-form pay-invoice-card'>
                    {renderCardContent()}
                </div>
            </div>
        </div>
    );
};

export default PayInvoicePage;
